/**
 * Box 5 (spec §3.4): the Campaign and Targeting tabs from the facts, the Ad
 * format and Creative tabs from the ads, the segment charts (FR-D2-11), the
 * naming coverage (FR-D2-06) and the Active filter's hidden count. A row
 * appears when it has a row in the filtered window, and "รวม" sums exactly
 * those rows (R16). The ad-grain tabs carry their coverage of the ad-set
 * spend (R17): Google PMax has no ads. NULL is never 0. Pure.
 *
 * @module services/channels/channelBreakdown
 */
import { creativeFormatOf, type CreativeFormat } from '@/services/creatives/creativeFormat';
import { groupRows, ratioOf } from '@/services/overview/overviewMath';
import { adsManagerUrlOf } from './adsManagerLinks';
import { isActiveIn } from './channelFilters';
import { breakdownMetricsOf, bySpendDesc, sumOf } from './channelMath';
import { idPrefix, targetingFamilyOf } from './channelNaming';
import {
  TARGETING_FAMILIES,
  type AdGrainCoverage, type AdGrainTable, type BreakdownTable, type CampaignBreakdownRow, type CampaignInfo,
  type CampaignState, type ChannelAdRow, type ChannelEntity, type ChannelFactRow, type ChannelPlatform,
  type CreativeBreakdownRow, type FormatBreakdownRow, type NamingCoverage, type SegmentPoint,
  type TargetingBreakdown, type TargetingBreakdownRow, type TargetingFamily,
} from './channelTypes';

export interface BreakdownContext {
  platform: ChannelPlatform;
  index: ReadonlyMap<string, CampaignInfo>;
  /** Workspace id → name; read only in all workspaces. */
  workspaceNames: Readonly<Record<string, string>>;
  allWorkspaces: boolean;
}

/** The state the Campaign table reads for a campaign the entities do not hold (a campaign id only the facts carry). */
const UNKNOWN: CampaignState = { kind: 'unknown' };

const campaignRowOf = (id: string, own: readonly ChannelFactRow[], ctx: BreakdownContext): CampaignBreakdownRow => {
  const c = ctx.index.get(id);
  return {
    key: id,
    name: c?.name ?? idPrefix(id),
    workspaceName: ctx.allWorkspaces ? (ctx.workspaceNames[own[0].workspaceId] ?? null) : null,
    type: c?.type ?? null,
    conforms: c?.conforms ?? false,
    nativeId: c?.nativeId ?? null,
    adsManagerUrl: adsManagerUrlOf(ctx.platform, c?.accountExternalId ?? null, c?.nativeId ?? null),
    state: c?.state ?? UNKNOWN,
    ...breakdownMetricsOf(own),
  };
};

/** One row per campaign with rows in the filtered window, biggest spend first. */
export const campaignTableOf = (
  rows: readonly ChannelFactRow[],
  ctx: BreakdownContext,
): BreakdownTable<CampaignBreakdownRow> => ({
  rows: groupRows(rows, (r) => r.campaignId)
    .map(([id, own]) => campaignRowOf(id, own, ctx))
    .sort(bySpendDesc<CampaignBreakdownRow>((r) => r.name)),
  total: breakdownMetricsOf(rows),
});

const segmentOf = (family: TargetingFamily, own: readonly TargetingBreakdownRow[], totalSpend: number | null): SegmentPoint => {
  const m = breakdownMetricsOf(own);
  return {
    family,
    share: ratioOf(m.spend, totalSpend),
    // lowSample is D1's isLowSample: known impressions under 500 leave the family out of the CTR and CPC charts.
    // Unreported impressions is not a small sample: such a family keeps its CPC, and its CTR is null as a ratio.
    ctr: m.lowSample ? null : m.ctr,
    cpc: m.lowSample ? null : m.cpc,
    lowSample: m.lowSample,
  };
};

/** FR-D2-11: one point per family present, in TARGETING_FAMILIES order, over the family's summed ad sets. */
export const segmentsOf = (rows: readonly TargetingBreakdownRow[]): SegmentPoint[] => {
  const totalSpend = sumOf(rows, (r) => r.spend);
  return TARGETING_FAMILIES
    .map((family) => ({ family, own: rows.filter((r) => r.family === family) }))
    .filter(({ own }) => own.length > 0)
    .map(({ family, own }) => segmentOf(family, own, totalSpend));
};

const adSetRowOf = (
  id: string,
  own: readonly ChannelFactRow[],
  adSets: ReadonlyMap<string, ChannelEntity>,
): TargetingBreakdownRow => {
  const name = adSets.get(id)?.adSetName ?? null;
  return { key: id, name: name ?? idPrefix(id), family: targetingFamilyOf(name), ...breakdownMetricsOf(own) };
};

/** One row per ad set. No segment charts on Google (S6.1): it targets by keyword / affinity, not by ad-group name. */
export const targetingTableOf = (
  rows: readonly ChannelFactRow[],
  adSets: ReadonlyMap<string, ChannelEntity>,
  platform: ChannelPlatform,
): TargetingBreakdown => {
  const tableRows = groupRows(rows, (r) => r.adSetId)
    .map(([id, own]) => adSetRowOf(id, own, adSets))
    .sort(bySpendDesc<TargetingBreakdownRow>((r) => r.name));
  return { rows: tableRows, total: breakdownMetricsOf(rows), segments: platform === 'google' ? null : segmentsOf(tableRows) };
};

const adSpendOf = (a: ChannelAdRow): number | null => a.spend;

/** D3 §3.4, shared with /creatives: the platform's value first, else the ad name; null = "ไม่ระบุ format". */
const formatOf = (a: ChannelAdRow): CreativeFormat | null => creativeFormatOf(a.adFormat, a.adName);

/**
 * R17, both shares 0–1.
 * - adShare: null when the ad-set spend is null or 0, or when every ad's spend
 *   is unreported. No ad row at all is a measured nothing (Google PMax has no
 *   ads), so the ads then hold 0 of a positive ad-set spend, not NULL.
 * - formatShare, three outcomes:
 *   - null when the ad spend is null or 0 (no ad row at all included);
 *   - 0 when the ad spend is known and above 0 and no ad has a format. An ad
 *     without a format is a known absence, so the formatted spend is a measured
 *     0 ("มี format 0%"). "A format" is the shared one (formatOf, R22): a name
 *     such as "Ad01 | 151026 | Static" counts, so Facebook reads about 99%;
 *   - null when ads with a format exist but none reports spend: an unreported
 *     numerator is not 0 (NULL is never 0). Some reporting is enough: the share
 *     is then over the ones that do, as the ad spend is summed.
 */
export const adGrainCoverageOf = (ads: readonly ChannelAdRow[], factSpend: number | null): AdGrainCoverage => {
  const adSpend = ads.length === 0 ? 0 : sumOf(ads, adSpendOf);
  const formatted = ads.filter((a) => formatOf(a) !== null);
  // Mirrors adSpend: nothing to sum is a measured 0; rows that all omit spend are NULL.
  const withFormat = formatted.length === 0 ? 0 : sumOf(formatted, adSpendOf);
  return { adShare: ratioOf(adSpend, factSpend), formatShare: ratioOf(withFormat, adSpend) };
};

/** '' is free for "no format": a CreativeFormat is never empty. */
const NO_FORMAT_KEY = '';

/** One row per shared format (Static / Video / Carousel / อื่น ๆ); no format ("ไม่ระบุ format") is a row of its own. */
export const formatTableOf = (ads: readonly ChannelAdRow[], factSpend: number | null): AdGrainTable<FormatBreakdownRow> => ({
  rows: groupRows(ads, (a) => formatOf(a) ?? NO_FORMAT_KEY)
    .map(([key, own]): FormatBreakdownRow => ({ key, format: formatOf(own[0]), ...breakdownMetricsOf(own) }))
    .sort(bySpendDesc<FormatBreakdownRow>((r) => r.format ?? NO_FORMAT_KEY)),
  total: breakdownMetricsOf(ads),
  coverage: adGrainCoverageOf(ads, factSpend),
});

/**
 * One row per ad, with its thumbnail (null = the placeholder) and its shared format. The name is the stored one, '' when
 * none: a blank or whitespace name is worded "(ไม่มีชื่อ) · <external id>" where it is drawn, as D3 does (creativeNameOf).
 */
export const creativeTableOf = (ads: readonly ChannelAdRow[], factSpend: number | null): AdGrainTable<CreativeBreakdownRow> => ({
  rows: groupRows(ads, (a) => a.adId)
    .map(([id, own]): CreativeBreakdownRow => ({
      key: id, name: own[0].adName ?? '', externalAdId: own[0].externalAdId, thumbnailUrl: own[0].thumbnailUrl,
      format: formatOf(own[0]), ...breakdownMetricsOf(own),
    }))
    .sort(bySpendDesc<CreativeBreakdownRow>((r) => r.name)),
  total: breakdownMetricsOf(ads),
  coverage: adGrainCoverageOf(ads, factSpend),
});

/** FR-D2-06, R16: conforming names ÷ the shown campaigns that spent; null when none did. */
export const namingCoverageOf = (rows: readonly CampaignBreakdownRow[]): NamingCoverage => {
  const spent = rows.filter((r) => r.spend !== null && r.spend > 0);
  const conforming = spent.filter((r) => r.conforms).length;
  return { conforming, withSpend: spent.length, share: spent.length === 0 ? null : conforming / spent.length };
};

/**
 * R16, "ซ่อน N": the non-Active campaigns (paused, ended, unknown) with rows under the other filters. A campaign the
 * entities do not hold counts as hidden: isActiveIn, the predicate activeRows drops by, says it is not Active.
 */
export const hiddenCountOf = (rows: readonly ChannelFactRow[], index: ReadonlyMap<string, CampaignInfo>): number =>
  new Set(rows.map((r) => r.campaignId).filter((id) => !isActiveIn(index, id))).size;
